import {
	Button,
	ButtonGroup,
	Card,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	stopPropagation,
	Switch,
	Text,
} from "@overmap-ai/blocks"
import { type AssetType, type ProcedureInitiative, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import {
	selectAssetTypeIdentifiersOfAssetType,
	selectAssetTypeStatusesOfAssetType,
	selectLatestAssetTypeFieldsOfAssetType,
	selectLatestProcedureTypeFieldsOfProcedureType,
	selectProcedureStepsOfProcedureType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import * as RadixCollapsible from "@radix-ui/react-collapsible"
import { useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, type MouseEventHandler, useCallback, useMemo } from "react"

import { interactiveCardCva } from "../../../cva"
import { FilterRenderer } from "../../../filtering/components"
import type { AnyFilter } from "../../../filtering/filter"
import { useFilterManager } from "../../../filtering/hooks"
import { useAssetDataFilterSchema } from "../../../filtering/schemas"
import { getProcedureDataFilterSchema } from "../../../filtering/schemas/ProcedureDataFilterSchema"
import { useFieldSchemasAndConditionsOfProcedureSteps } from "../../../hooks/procedures"
import { modelIconMapping } from "../../../mappings/icons"
import {
	hideAssetType,
	setActiveProcedureInitiative,
	setAssetTypeMapFilters,
	setProcedureInitiativeMapFilters,
	setShowAssetLabels,
	unhideAssetType,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../../store"
import {
	selectActiveProcedureInitiativeOfAssetType,
	selectActiveProjectId,
	selectAssetsOfProjectAndAssetType,
	selectAssetTypeMapFiltersById,
	selectIsAssetTypeHidden,
	selectProcedureInitiativeMapFiltersById,
	selectProcedureInitiativesOfProjectAndAssetType,
	selectShowAssetLabels,
} from "../../../store/selectors"
import type { AssetData } from "../../../typings"
import { ProcedureInitiativeDropdownSelect } from "../../DropdownSelects/ProcedureInitiativeDropdownSelect"
import { AssetDataMapFilterMenu } from "../../FilterMenus/AssetDataMapFilterMenu"
import { useDeserializeFieldSchema } from "../../Form/hooks"
import { AssetTypeIcon } from "../../Icons/AssetTypeIcon"
import { ProcedureTypeIcon } from "../../Icons/ProcedureTypeIcon"
import { ProcedureInitiativeIndexLabel } from "../../IndexLabels/ProcedureInitiativeIndexLabel"

interface AssetTypeVisibilityItemProps {
	assetType: Stored<AssetType>
}

export const AssetTypeVisibilityItem = memo<AssetTypeVisibilityItemProps>((props) => {
	const { assetType } = props

	const activeProjectId = useAppSelector(selectActiveProjectId)!
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const latestAssetTypeFields = useAppSelector(selectLatestAssetTypeFieldsOfAssetType(assetType.uuid))
	const isHidden = useAppSelector(selectIsAssetTypeHidden(assetType.uuid))
	const showAssetLabels = useAppSelector(selectShowAssetLabels(assetType.uuid))
	const users = useShallowEqaulSelector(selectUsersOfProject(activeProjectId))
	const assets = useAppSelector(selectAssetsOfProjectAndAssetType(activeProjectId, assetType.uuid))
	const serializedAssetTypeMapFilters = useAppSelector(selectAssetTypeMapFiltersById(assetType.uuid))
	const assetTypeIdentifiers = useShallowEqaulSelector(selectAssetTypeIdentifiersOfAssetType(assetType.uuid))
	const assetTypeStatuses = useShallowEqaulSelector(selectAssetTypeStatusesOfAssetType(assetType.uuid))
	const procedureInitiatives = useShallowEqaulSelector(
		selectProcedureInitiativesOfProjectAndAssetType(activeProjectId, assetType.uuid),
	)
	const activeProcedureInitiative = useAppSelector(selectActiveProcedureInitiativeOfAssetType(assetType.uuid))
	const procedureTypeFields = useAppSelector(
		selectLatestProcedureTypeFieldsOfProcedureType(activeProcedureInitiative?.procedure_type ?? ""),
	)

	const procedureSteps = useShallowEqaulSelector(
		selectProcedureStepsOfProcedureType(activeProcedureInitiative?.procedure_type ?? ""),
	)
	const procedureStepFieldSchemasAndConditions = useFieldSchemasAndConditionsOfProcedureSteps(procedureSteps)
	const serializedActiveProcedureInitiativeFilters = useAppSelector(
		selectProcedureInitiativeMapFiltersById(activeProcedureInitiative?.uuid ?? ""),
	)

	const assetTypeFieldSchema = useDeserializeFieldSchema(latestAssetTypeFields?.fields ?? EMPTY_ARRAY)

	const assetDataFilterSchema = useAssetDataFilterSchema({
		assetTypeIdentifiers,
		assetTypeStatuses,
		assetTypeFieldSchema,
	})

	const assetDataFilters = useMemo(() => {
		return assetDataFilterSchema.deserializeFilters(serializedAssetTypeMapFilters)
	}, [assetDataFilterSchema, serializedAssetTypeMapFilters])

	const handleAssetDataFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setAssetTypeMapFilters({
					id: assetType.uuid,
					filters: assetDataFilterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, assetType.uuid, assetDataFilterSchema],
	)

	const assetDataFilterManager = useFilterManager<AssetData>({
		filterSchema: assetDataFilterSchema,
		filters: assetDataFilters,
		onFilterChange: handleAssetDataFiltersChange,
	})

	const procedureTypeFieldSchema = useDeserializeFieldSchema(procedureTypeFields?.fields ?? EMPTY_ARRAY)

	const procedureDataFilterSchema = useMemo(() => {
		return getProcedureDataFilterSchema({
			procedureInitiatives,
			procedureTypeFieldSchema,
			procedureSteps,
			procedureStepFieldSchemasAndConditions,
			users,
		})
	}, [procedureInitiatives, procedureTypeFieldSchema, procedureSteps, procedureStepFieldSchemasAndConditions, users])

	const filters = useMemo(() => {
		return procedureDataFilterSchema.deserializeFilters(serializedActiveProcedureInitiativeFilters)
	}, [procedureDataFilterSchema, serializedActiveProcedureInitiativeFilters])

	const handleActiveProcedureInitiativeFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			if (!activeProcedureInitiative?.uuid) return

			dispatch(
				setProcedureInitiativeMapFilters({
					id: activeProcedureInitiative.uuid,
					filters: procedureDataFilterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, procedureDataFilterSchema, activeProcedureInitiative?.uuid],
	)

	const procedureDataFilterManager = useFilterManager({
		filterSchema: procedureDataFilterSchema,
		filters,
		onFilterChange: handleActiveProcedureInitiativeFiltersChange,
	})

	const handleCheckedChange = useCallback<MouseEventHandler>(
		(e) => {
			e.preventDefault()
			e.stopPropagation()
			dispatch(isHidden ? unhideAssetType(assetType.uuid) : hideAssetType(assetType.uuid))
		},
		[assetType.uuid, dispatch, isHidden],
	)

	const handleShowAssetLabelsCheckedChange = useCallback(
		(checked: boolean) => {
			dispatch(setShowAssetLabels({ assetTypeId: assetType.uuid, showLabels: checked }))
		},
		[assetType.uuid, dispatch],
	)

	const handleCollapsibleOpenChange = useCallback(
		(open: boolean) => {
			dispatch(open ? unhideAssetType(assetType.uuid) : hideAssetType(assetType.uuid))
		},
		[assetType.uuid, dispatch],
	)

	const handleActiveProcedureInitiativeChange = useCallback(
		(procedureInitiative?: Stored<ProcedureInitiative>) => {
			dispatch(
				setActiveProcedureInitiative({
					assetTypeId: assetType.uuid,
					procedureInitiativeId: procedureInitiative?.uuid ?? null,
				}),
			)
		},
		[assetType.uuid, dispatch],
	)

	const handleView = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/$projectId/asset-types/$assetTypeId",
			params: (params) => ({ ...params, assetTypeId: assetType.uuid }),
		})
	}, [assetType.uuid, navigate])

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId",
			to: "/$organizationId/settings/asset-types/$assetTypeId",
			params: (params) => ({ ...params, assetTypeId: assetType.uuid }),
		})
	}, [assetType.uuid, navigate])

	const handleRemoveAllFilters = useCallback(() => {
		assetDataFilterManager.removeAll()
		procedureDataFilterManager.removeAll()
	}, [assetDataFilterManager, procedureDataFilterManager])

	const isFilters =
		assetDataFilterManager.getFilters().length > 0 || procedureDataFilterManager.getFilters().length > 0

	return (
		<RadixCollapsible.Root open={!isHidden} onOpenChange={handleCollapsibleOpenChange} asChild>
			<Card role="listitem" className={cx("flex h-max w-full flex-col gap-4", interactiveCardCva())} size="sm">
				<RadixCollapsible.Trigger asChild>
					<div className="group flex w-full cursor-pointer items-center gap-2" tabIndex={0}>
						<div className="flex min-w-0 grow items-center gap-2">
							<AssetTypeIcon assetTypeId={assetType.uuid} />
							<Text className="truncate">{assetType.name}</Text>
							<Text size="sm" accentColor="base" weight="regular" className="whitespace-nowrap">
								{assets.length}
							</Text>
						</div>

						<ButtonGroup className="flex items-center gap-1" size="sm" variant="ghost" accentColor="base">
							<IconButton onClick={handleCheckedChange}>
								<LuIcon icon={!isHidden ? modelIconMapping.visible : modelIconMapping.hidden} />
							</IconButton>
							<Menu.Root>
								<Menu.ClickTrigger>
									<IconButton aria-label="more" onClick={stopPropagation}>
										<LuIcon icon={modelIconMapping.more} />
									</IconButton>
								</Menu.ClickTrigger>
								<Menu.Content size="sm">
									<Menu.Item onSelect={handleView}>
										<LuIcon icon={modelIconMapping.expand} />
										View
									</Menu.Item>
									<Menu.Item onSelect={handleEdit}>
										<LuIcon icon={modelIconMapping.edit} />
										Edit
									</Menu.Item>
								</Menu.Content>
							</Menu.Root>
						</ButtonGroup>
					</div>
				</RadixCollapsible.Trigger>
				<RadixCollapsible.Content className="flex flex-col gap-4">
					<Separator size="full" />
					<div className="flex items-center justify-between gap-2">
						<Text size="sm">Asset labels</Text>
						<Switch.Root
							aria-label={`Asset labels for ${assetType.name}`}
							checked={showAssetLabels}
							onCheckedChange={handleShowAssetLabelsCheckedChange}
							size="xs"
							radius="full"
						>
							<Switch.Thumb />
						</Switch.Root>
					</div>
					<div className="flex flex-col gap-2">
						<ButtonGroup className="flex justify-between gap-2" size="sm" variant="soft" accentColor="base">
							<ProcedureInitiativeDropdownSelect
								required={false}
								procedureInitiatives={procedureInitiatives}
								value={activeProcedureInitiative ?? undefined}
								onValueChange={handleActiveProcedureInitiativeChange}
								size="sm"
							>
								<Button size="sm" variant="soft" accentColor="base" className="shrink-1 w-max min-w-0">
									{activeProcedureInitiative ? (
										<>
											<ProcedureTypeIcon
												procedureTypeId={activeProcedureInitiative.procedure_type}
											/>
											<ProcedureInitiativeIndexLabel
												className="truncate"
												procedureInitiativeId={activeProcedureInitiative.uuid}
											/>
										</>
									) : (
										<>Select initiative</>
									)}
								</Button>
							</ProcedureInitiativeDropdownSelect>

							<div className="flex gap-2">
								<AssetDataMapFilterMenu
									assetDataFilterManager={assetDataFilterManager}
									assetTypeIdentifiers={assetTypeIdentifiers}
									assetTypeStatuses={assetTypeStatuses}
									assetTypeFieldSchema={assetTypeFieldSchema}
									procedureDataFilterManager={procedureDataFilterManager}
									procedureSteps={procedureSteps}
									procedureStepFieldSchemasAndConditions={procedureStepFieldSchemasAndConditions}
									procedureTypeFieldSchema={procedureTypeFieldSchema}
									size="sm"
									align="start"
								>
									<Button onClick={stopPropagation} className="w-max">
										<LuIcon icon={modelIconMapping.filter} />
										Filter
									</Button>
								</AssetDataMapFilterMenu>

								{isFilters && (
									<IconButton onClick={handleRemoveAllFilters}>
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								)}
							</div>
						</ButtonGroup>

						<FilterRenderer filterManager={assetDataFilterManager} size="xs" />

						<FilterRenderer filterManager={procedureDataFilterManager} size="xs" />
					</div>
				</RadixCollapsible.Content>
			</Card>
		</RadixCollapsible.Root>
	)
})
AssetTypeVisibilityItem.displayName = "AssetTypeVisibilityItem"
